import { Component, Injector, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { from, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { I18nService } from '@recruitee/i18n';
import { EnabledIntegrationKindsActions } from '@recruitee/integrations-data';
import { DialogService } from '@recruitee/user-interface';

import { IntegrationEventActions } from '../../common/events/events';
import { HttpErrorHandlerService } from '../../common/services/http-error-handler.service';
import { IntegrationListLegalDialogData } from '../../misc/integration-list-legal-dialog/integration-list-legal-dialog.component';
import { XingApiService } from './xing-api';

@Component({
  selector: 'rt-xing-container',
  template: '',
  standalone: true,
})
export class XingContainerComponent implements OnDestroy {
  public partnerName = this.i18n.translate('app.settings.integrations.xing.partnerName');
  public kind = 'xing';

  public confirmDialogConfig: IntegrationListLegalDialogData = {
    partnerName: this.partnerName,
    helpArticleUrl: null,
    buttonText: this.i18n.translate('rti.common.settings.legal_dialog.accept'),
    textOverClaims: this.i18n.translate('rti.common.settings.legal_dialog.paragraph_1', {
      buttonText: this.i18n.translate('rti.common.settings.legal_dialog.accept'),
    }),
    claims: [
      this.i18n.translate('rti.common.settings.legal_dialog.list.1'),
      this.i18n.translate('rti.common.settings.legal_dialog.list.2', {
        partner_name: this.partnerName,
      }),
      this.i18n.translate('rti.common.settings.legal_dialog.list.3', {
        partner_name: this.partnerName,
      }),
    ],
  };

  public removeActionConfig = {
    handler: () => this.callRemoveIntegration(),

    dialogConfig: {
      title: this.i18n.translate('app.settings.integrations.xing.marketplace_remove_dialog.title'),
      variant: 'error',
      agreeLabel: this.i18n.translate(
        'app.settings.integrations.xing.marketplace_remove_dialog.agree_label',
      ),
      message: this.i18n.translate(
        'app.settings.integrations.xing.marketplace_remove_dialog.message',
      ),
      safeFooter: true,
      cancelLabel: this.i18n.translate('rti.common.cancel'),
    },
  };
  private ngOnDestroy$ = new Subject<void>();
  constructor(
    private i18n: I18nService,
    private injector: Injector,
    private dialogService: DialogService,
    private store: Store,
    private xingApi: XingApiService,
    protected httpErrorHandlerService: HttpErrorHandlerService,
  ) {}

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public remove(): void {
    this.dialogService.warning(this.removeActionConfig.dialogConfig).subscribe(confirmed => {
      if (!confirmed) return;
      this.removeActionConfig.handler();
    });
  }

  public integrate(): void {
    from(import('../../misc/integration-list-legal-dialog/integration-list-legal-dialog.component'))
      .pipe(
        switchMap(({ IntegrationListLegalDialogComponent, IntegrationListLegalDialogConfig }) =>
          this.dialogService
            .openCustom(
              IntegrationListLegalDialogComponent,
              { ...IntegrationListLegalDialogConfig, injector: this.injector },
              this.confirmDialogConfig,
            )
            .afterClosed.pipe(map(result => !!result?.accepted)),
        ),
      )
      .subscribe(accepted => {
        if (accepted) this.callIntegrateIntegration();
      });
  }

  private callIntegrateIntegration() {
    this.xingApi
      .post('integration', {})
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(
        () => {
          this.store.dispatch(EnabledIntegrationKindsActions.fetch());
          this.store.dispatch(
            IntegrationEventActions.IntegrationUpdatedEvent({ payload: { uid: this.kind } }),
          );
        },
        errorResponse => {
          this.httpErrorHandlerService.default(errorResponse);
        },
      );
  }

  private callRemoveIntegration() {
    this.xingApi
      .delete('integration')
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(
        () => {
          this.store.dispatch(EnabledIntegrationKindsActions.fetch());
          this.store.dispatch(
            IntegrationEventActions.IntegrationRemovedEvent({ payload: { uid: this.kind } }),
          );
        },
        errorResponse => {
          this.httpErrorHandlerService.default(errorResponse);
        },
      );
  }
}
